import fb from '../../firebase'
import { CefApplianceState, RootState } from '../types'
import { moduleActionContext } from '..'
import { ActionContext } from 'vuex'
import { DeepPartial, KeyDict } from '@/types'
import { CollectionNames } from '@/modules/database/utils'
import store from '@/store'
import { lazyUpdate } from '../storehelper'
import firebase from 'firebase/app'
import { CefAppliance, CefApplianceAddParams, CefApplianceCastStatusChangeType, CefAppUserType, CefOutput,
         CefOutputType, CefRenderer, CefRendererType
       } from '@/modules/cefappliance/types'
import Vue from 'vue'
import { SystemStatus } from '@/types/ops'
import router from '@/router'
import { CallTriage, CastStatus } from '@/types/casts'
import { LogLevel } from '@/types/logs'
import { defineModule } from 'direct-vuex'
import studio from '@/modules/database/utils'
import { generateRandomString } from '@/modules/common/utils'
import { MergeOption } from '@/modules/database/utils/database'
import { addCastStatusChange } from '@/modules/cefappliance/utils/cefappliance_caststats'
import { FirestoreQuerySnapper, FirestoreSnapperType } from '@/modules/database/utils/databaseQuerySubscriptions'
import { Result, ResultVoid } from '@/modules/errorHandling/results'
import FirebasePresence from '@/classes/FirebasePresence'
import { CastFanRoom } from '@/modules/casts/classes/CastFanRoom'

type CefApplianceContext = ActionContext<CefApplianceState, RootState>

const checkIfApplianceExistsOrThrowErrors = async (state: CefApplianceState): Promise<string> => {
  if (!state.cefApplianceId) throw Error('You need to be subscribed to a cefAppliance first')
  const doc = await studio.db.collection(CollectionNames.CEFAPPLIANCE).doc(state.cefApplianceId).get()
  if (!doc.isSuccess) throw Error('Cef Appliance does not exists')
  return state.cefApplianceId
}

const createCefUri = (castId: string): string => {
  const payload = {
    name: 'cefEvent',
    params: { id: castId },
    query: { token: 'C7dDTNZXoS2wxlnNqjXo', 'cef-mode': 'full' }
  }
  const { href } = router.resolve(payload)
  return window.location.origin + href
}

export const MAX_CASTER_PREVIEWS = 5

let roomLiveTallyTimeout: number|null = null

const clearTallyTimeout = () => {
  if (roomLiveTallyTimeout !== null) {
    clearTimeout(roomLiveTallyTimeout)
  }
  roomLiveTallyTimeout = null
}

const DEFAULT_CEF_RENDERER_URI = 'https://google.com'

const addStandardCefrenderersInfo = (
  cefRenderers: KeyDict<Partial<CefRenderer>>, // If not Partial, complains about possibly missing 'outputs' key.
  wrtcServer: string,
  name: string,
  machineId: string,
  isBackup: boolean = false
) => {
  const outputsKey = isBackup ? 'outputs_backup' : 'outputs'
  const statusKey = isBackup ? 'status_backup' : 'status'

  for (let index = 1; index <= 8; ++index) {
    const key = `cef${index}`
    if (cefRenderers[key] === undefined) cefRenderers[key] = {}
    const cefRenderer = cefRenderers[key]
    cefRenderer[statusKey] = SystemStatus.NONE
    cefRenderer.type = cefRenderer.type ?? CefRendererType.Room
    cefRenderer.uri = cefRenderer.uri ?? DEFAULT_CEF_RENDERER_URI //Will be empty once dropdown options are set up
    const outputs: KeyDict<CefOutput> = {}
    addPreviewOutput(outputs, wrtcServer, name, machineId, key)
    cefRenderer[outputsKey] = outputs
    cefRenderer.castId = cefRenderer.castId !== undefined ? cefRenderer.castId : null
  }
}

const addPreviewOutput = (
  outputs: KeyDict<CefOutput>,
  wrtcServer: string,
  name: string,
  machineId: string,
  key: string,
) => {
  outputs.output1 = {
    status: SystemStatus.NONE,
    type: CefOutputType.Preview,
    uri: window.encodeURI(`rtsp://${wrtcServer}:1935/livetcp/${name}-${machineId}-${key}`)
  }
}

const addBackupCefApplianceInfo = (cefAppliance: CefAppliance, params: CefApplianceAddParams) => {
  if (params.mixerRegionBackup === undefined) {
    throw new Error('Cannot add backup info without backup machine id.')
  }
  const transcodingregionBackup = store.state.admin.transcodingregions[params.previewRegionBackup || params.previewRegion]
  if (transcodingregionBackup === undefined) {
    throw new Error('Could not find backup transcoding region, have you subscribed to admin.subscribeAdminProperties?')
  }

  const wrtcServerBackup = Object.values(transcodingregionBackup.wrtcserver)[0].url
  const isBackup = true
  const previewId = generateRandomString(8)
  const name = params.name.replace(/\W/g, '')
  addStandardCefrenderersInfo(cefAppliance.cefrenderers, wrtcServerBackup, name, previewId, isBackup)

  cefAppliance.cefrenderers.cefBackground.status_backup = SystemStatus.NONE
  cefAppliance.cefrenderers.cefForeground.status_backup = SystemStatus.NONE
  cefAppliance.cefrenderers.extern.status_backup = SystemStatus.NONE

  cefAppliance.healthcheck_watchdog_backup = 0
  cefAppliance.mixer_status_backup = SystemStatus.NONE
  cefAppliance.region_backup = params.mixerRegionBackup

  const bgOutputs: KeyDict<CefOutput> = {}
  addPreviewOutput(bgOutputs, wrtcServerBackup, name, previewId, 'cefBackground')
  cefAppliance.cefrenderers.cefBackground.outputs_backup = bgOutputs
  const exOutputs: KeyDict<CefOutput> = {}
  addPreviewOutput(exOutputs, wrtcServerBackup, name, previewId, 'extern')
  cefAppliance.cefrenderers.extern.outputs_backup = exOutputs

  cefAppliance.outputs_backup = {
    output1: {
      status: SystemStatus.NONE,
      type: CefOutputType.Broadcast,
      uri: ''
    },
    outputPreview: {
      status: SystemStatus.NONE,
      type: CefOutputType.Preview,
      uri: `rtsp://${wrtcServerBackup}:1935/livetcp/cef2srt${previewId}`
    }
  }
}

const cefApplianceModule = defineModule({
  namespaced: true,
  state: {
    cefAppUserType: CefAppUserType.Master,
    cefAppliance: null,
    cefApplianceId: null, //Refers to document ID in database
    cefApplianceList: {},
    showPreviewCasterIds: []
  } as CefApplianceState,
  getters: {
    hearthbeatState: (state: CefApplianceState) => (currentTime: number, forBackup: boolean = false): SystemStatus => {
      if (state.cefAppliance === null) return SystemStatus.NONE
      let watchdog = state.cefAppliance.healthcheck_watchdog
      let mixerStatus = state.cefAppliance.mixer_status
      if (forBackup) {
        watchdog = state.cefAppliance.healthcheck_watchdog_backup ?? 0
        mixerStatus = state.cefAppliance.mixer_status_backup ?? SystemStatus.ERROR
      }
      if (watchdog < currentTime / 1000 - 120) return SystemStatus.ERROR
      if (watchdog < currentTime / 1000 - 60) return SystemStatus.WARNING
      return mixerStatus
    },
    cefAppliancePageActive (state: CefApplianceState): boolean {
      return !!state.cefApplianceId
    },
    previewOutput: (state: CefApplianceState) => (forBackup: boolean = false): string|null => {
      const outputsProp = forBackup ? 'outputs_backup' : 'outputs'
      return state.cefAppliance?.[outputsProp]?.outputPreview?.uri ?? null
    },
    isModerator: (state: CefApplianceState) => (): boolean => {
      return [CefAppUserType.Moderator, CefAppUserType.Master].includes(state.cefAppUserType)
    },
    isProducer: (state: CefApplianceState) => (): boolean => {
      return [CefAppUserType.Producer, CefAppUserType.Master].includes(state.cefAppUserType)
    }
  },
  actions: {
    async subscribe (context: CefApplianceContext, applianceId: string): Promise<ResultVoid<'subscription'>> {
      const key = `cefappliance_${applianceId}`
      const foundKey = studio.subscriptions.runningKeys().find(key => key.startsWith('cefappliance_'))
      if (foundKey !== undefined && key !== foundKey) {
        throw new Error(`Cannot subscribe to multiple appliances original:${foundKey}, new: ${applianceId}`)
      }
      const { commit } = cefApplianceModuleActionContext(context)
      commit.setCurrentCefApplianceId(applianceId)
      const result = await studio.subscriptions.subscribe({
        key,
        query: studio.db.collection(CollectionNames.CEFAPPLIANCE).doc(applianceId),
        callback: commit.updateCefAppliance,
        waitForIt: true
      })
      if (result.isFailure) return result.convert()
      /* needs further refactoring
       * -> make a presence and add it to the database
       * -> the CefAppliance component will then add casts to it
       */
      const sessionId = store.state.user.sessionId
      const currentUserId = store.state.user.id
      if (store.state.user.presence?.sessionId !== sessionId && currentUserId !== null) {
        const presence = new FirebasePresence({ sessionId, uid: currentUserId, castId: undefined })
        store.dispatch.user.setPresence(presence)
      }
      return Result.ok()
    },
    unsubscribe (context: CefApplianceContext, applianceId: string) {
      const { commit } = cefApplianceModuleActionContext(context)
      studio.subscriptions.unsubscribe(`cefappliance_${applianceId}`, commit.clearCefAppliance)
    },
    async subscribeList (context: CefApplianceContext) {
      const { commit } = cefApplianceModuleActionContext(context)
      const snapper: FirestoreQuerySnapper = {
        query: fb.db.collection(CollectionNames.CEFAPPLIANCE)
                 .where('team_id', '==', store.getters.user.teamId),
        mutation: commit.updateCefApplianceList,
        type: FirestoreSnapperType.Query
      }
      const errorCallback = undefined
      const waitForIt = true
      await studio.subscriptions.subscribeFB('cefappliancelist', snapper, errorCallback, waitForIt)
    },
    unsubscribeList (context: CefApplianceContext) {
      const { commit } = cefApplianceModuleActionContext(context)
      studio.subscriptions.unsubscribeFB('cefappliancelist', commit.clearCefAppliance)
    },
    async updateStationSlate (context: CefApplianceContext, slate: string) {
      const { state } = cefApplianceModuleActionContext(context)
      if (state.cefApplianceId === null) throw new Error('Cannot update slate if you are not subscribed to a cefappliance')
      const result = await studio.db.collection(CollectionNames.CEFAPPLIANCE).doc(state.cefApplianceId).set({
        station: {
          slate
        }
      }, MergeOption.MERGE)
      if (!result.isSuccess) {
        result.log('Could not update Station Slate')
      }
    },
    async updateStationSlateShort (context: CefApplianceContext, slate: string) {
      const { state } = cefApplianceModuleActionContext(context)
      if (state.cefApplianceId === null) throw new Error('Cannot update slate if you are not subscribed to a cefappliance')
      const result = await studio.db.collection(CollectionNames.CEFAPPLIANCE).doc(state.cefApplianceId).set({
        station: {
          slateShort: slate
        }
      }, MergeOption.MERGE)
      if (!result.isSuccess) {
        result.log('Could not update Station Slate')
      }
    },
    async add (_context: CefApplianceContext, params: CefApplianceAddParams): Promise<string> {
      const newDocRef = fb.db.collection(CollectionNames.CEFAPPLIANCE).doc()
      const name = params.name.replace(/\W/g, '')
      const transcodingregion = store.state.admin.transcodingregions[params.previewRegion] //Currently unused
      if (transcodingregion === undefined) {
        throw new Error('Could not find transcoding region, have you subscribed to admin.subscribeAdminProperties?')
      }
      if (store.state.team.team === null) {
        throw new Error('Cannot add a cefappliance to a null team')
      }
      const wrtcServer = Object.values(transcodingregion.wrtcserver)[0].url
      const cefrenderers: KeyDict<CefRenderer> = {}
      const previewId = generateRandomString(8)
      addStandardCefrenderersInfo(cefrenderers, wrtcServer, name, previewId)
      const backgroundUrl = window.location.origin + router.resolve({
        name: 'cefApplianceGraphics',
        params: {
          cefApplianceId: newDocRef.id,
          type: CefRendererType.Background
        },
        query: { token: 'C7dDTNZXoS2wxlnNqjXo' }
      }).href
      const bgOutputs: KeyDict<CefOutput> = {}
      addPreviewOutput(bgOutputs, wrtcServer, name, previewId, 'cefBackground')
      cefrenderers.cefBackground = {
        buffersize: 0,
        status: SystemStatus.NONE,
        type: CefRendererType.Background,
        uri: backgroundUrl,
        params: {
          assetUrl: 'https://kiswe-olympics.s3.amazonaws.com/beijing/Athlete+Moment+Stay+Tuned+Slates/Blue+-+Stay+Tuned.webm'
        },
        castId: null,
        outputs: bgOutputs
      }
      const exOutputs: KeyDict<CefOutput> = {}
      addPreviewOutput(exOutputs, wrtcServer, name, previewId, 'extern')
      cefrenderers.extern = {
        buffersize: 0,
        status: SystemStatus.NONE,
        type: CefRendererType.Extern,
        uri: 'rtmp://wrtc-va1.kiswe.com/live/bigbuckbunny',
        castId: null,
        outputs: exOutputs
      }
      const foregroundUrl = window.location.origin + router.resolve({
        name: 'cefApplianceGraphics',
        params: {
          cefApplianceId: newDocRef.id,
          type: CefRendererType.Bumper
        },
        query: { token: 'C7dDTNZXoS2wxlnNqjXo' }
      }).href
      cefrenderers.cefForeground = {
        buffersize: 0,
        status: SystemStatus.NONE,
        type: CefRendererType.Bumper,
        uri: foregroundUrl,
        params: {
          assetUrl: 'https://kiswe-olympics.s3.amazonaws.com/studio-slates/aom_long.webm',
          assetUrlShort: 'https://kiswe-olympics.s3.amazonaws.com/studio-slates/aom_short.webm'
        },
        castId: null,
        outputs: {}
      }
      const srtId = generateRandomString(8)
      const newCefAppliance = CefAppliance.load({
        name: params.name,
        deleted: false,
        healthcheck_watchdog: 0,
        inputselection: 'cefBackground',
        machineid: null,
        machineid_backup: null,
        region: params.mixerRegion,
        status: CastStatus.Unknown,
        start_date: params.startDate,
        liveClipping: params.liveClipping,
        end_date: params.endDate,
        switchDelay: 1000,
        mixer_status: SystemStatus.NONE,
        cefrenderers,
        outputs: {
          output1: {
            status: SystemStatus.NONE,
            type: CefOutputType.Broadcast,
            uri: ''
          },
          output2: {
            status: SystemStatus.NONE,
            type: CefOutputType.Broadcast,
            uri: ''
          },
          outputPreview: {
            status: SystemStatus.NONE,
            type: CefOutputType.Preview,
            uri: `rtsp://${wrtcServer}:1935/livetcp/cef2srt${srtId}`
          }
        },
        source_streams: {},
        station: {
          slate: 'https://kiswe-olympics.s3.amazonaws.com/studio-slates/connecting.webm',
          slateShort: 'https://kiswe-olympics.s3.amazonaws.com/studio-slates/connecting.webm'
        },
        team_id: store.state.team.activeTeam,
        version: store.state.team.team.version
      })

      if (!newCefAppliance.isSuccess) {
        throw new Error(newCefAppliance.message)
      }

      if (!!params.mixerRegionBackup) {
        addBackupCefApplianceInfo(newCefAppliance.value, params)
      }
      if (params.station !== null) {
        const result = await store.dispatch.station.update({ id: params.station, cefappliance: newDocRef.id })
        if (!result.isSuccess) {
          throw new Error('Could not update station with id ' + params.station)
        }
      }
      const result2 = await studio.db.collection(CollectionNames.CEFAPPLIANCE).doc(newDocRef.id).set(newCefAppliance.value, MergeOption.OVERWRITE)
      if (!result2.isSuccess) throw new Error(`Could not add cefappliance to database + ${result2.message}`)
      return newDocRef.id
    },
    async delete (_context: CefApplianceContext, id: string) {
      const result = await studio.db.collection(CollectionNames.CEFAPPLIANCE).doc(id).set({
        end_date: new Date(),
        deleted: true
      }, MergeOption.MERGE)
      if (!result.isSuccess) {
        console.error('Could not delete cefappliance', result.error)
      }
    },
    async setApplianceName (context: CefApplianceContext,  name: string) {
      const { state } = cefApplianceModuleActionContext(context)
      const id = await checkIfApplianceExistsOrThrowErrors(state)
      fb.db.collection(CollectionNames.CEFAPPLIANCE).doc(id).set({ name } as Partial<CefAppliance>, { merge: true })
    },
    async setAssetUrl (context: CefApplianceContext, params: { cefId: string, url: string, urlShort?: string }) {
      const { state } = cefApplianceModuleActionContext(context)
      const { cefId, url, urlShort } = params
      const id = await checkIfApplianceExistsOrThrowErrors(state)
      const renderers = state.cefAppliance?.cefrenderers
      if (renderers === undefined) {
        throw new Error('setAssetUrl::renderers is not suppose to be undefined')
      }
      if (![CefRendererType.Background, CefRendererType.Bumper].includes(renderers[cefId]?.type)) {
        throw new Error('cefId is not valid for assetUrl assignment')
      }
      const update: DeepPartial<CefAppliance> = {
        cefrenderers: {
          [cefId]: {
            params: {
              assetUrl: url
            }
          }
        }
      }
      if (urlShort) {
        // @ts-ignore
        update.cefrenderers[cefId].params.assetUrlShort = urlShort
      }
      const result = await studio.db.collection(CollectionNames.CEFAPPLIANCE).doc(id).set(update , MergeOption.MERGE)
      result.logIfError('Could not set assetUrl')
      store.dispatch.logs.addLog({
        message: 'Updated assetUrl of CefRenderer',
        level: LogLevel.Info,
        data: {
          applianceId: id,
          cefId: cefId,
          assetUrl: url
        }
      })
    },
    async setOutput (context: CefApplianceContext, params: { uri: string, forBackup?: boolean, id: string }) {
      const { state } = cefApplianceModuleActionContext(context)
      const id = await checkIfApplianceExistsOrThrowErrors(state)
      const outputsProp = params.forBackup ? 'outputs_backup' : 'outputs'
      const result = await studio.db.collection(CollectionNames.CEFAPPLIANCE).doc(id).set({
        [outputsProp]: {
          [params.id]: {
            uri: params.uri
          }
        }
      }, MergeOption.MERGE)
      result.logIfError('Could not update output')
    },
    async setInput (context: CefApplianceContext, params: { uri: string, cefId: string, streamId?: string }) {
      const { state } = cefApplianceModuleActionContext(context)
      const id = await checkIfApplianceExistsOrThrowErrors(state)
      const renderers = state.cefAppliance?.cefrenderers
      if (renderers === undefined) {
        throw new Error('setInput::renderers is not suppose to be undefined')
      }
      if (!Object.keys(renderers).includes(params.cefId)) {
        throw new Error('cefId is not a valid id for a cefrenderer')
      }
      const updatedCefRenderer: Partial<CefRenderer> = { uri: params.uri }
      if (params.streamId) {
        updatedCefRenderer.buffersize = 5
        updatedCefRenderer.streamId = params.streamId
      }
      await fb.db.collection(CollectionNames.CEFAPPLIANCE).doc(id).set({
        cefrenderers: { [params.cefId]: updatedCefRenderer }
      } as DeepPartial<CefAppliance>, { merge: true })
    },
    async setCast (context: CefApplianceContext, params: { cefId: string, cast: CastFanRoom|null }) {
      const { state } = cefApplianceModuleActionContext(context)
      const id = await checkIfApplianceExistsOrThrowErrors(state)
      const renderers = state.cefAppliance?.cefrenderers
      if (renderers === undefined) {
        throw new Error('setCast::renderers is not supposed to be undefined')
      }
      const cefRenderer = renderers[params.cefId]
      if (!cefRenderer) {
        throw new Error('cefId is not a valid id for a cefrenderer')
      }
      const payload = {
        cefrenderers: {
          [params.cefId]: {
            castId: params.cast?.id || null,
            uri: params.cast ? createCefUri(params.cast.id) : DEFAULT_CEF_RENDERER_URI
          }
        }
      }
      try {
        const previousCastId = cefRenderer.castId
        if (previousCastId) {
          const result = await store.dispatch.casts.setTally({ castId: previousCastId, message: null})
          if (!result.isSuccess) {
            console.error('Could not set Tally of previous active cast', result.error)
          }
          addCastStatusChange(previousCastId, {
            cefApplianceId: state.cefApplianceId,
            status: CefApplianceCastStatusChangeType.Cleared,
            teamId: store.state.team.activeTeam
          })
        }
        if (params.cast !== null) {
          // Move all deleted users back to queue so they have to come through moderation again
          const invitedCasters = params.cast.invited_casters
          for (const [id, caster] of Object.entries(invitedCasters)) {
            if (!caster.deleted || !caster.anonymous) continue
            if (caster.triage !== CallTriage.Accepted) continue
            invitedCasters[id].triage = CallTriage.Queued
          }
          // Make the cast "active" when assigning it to the CefRenderer.
          await store.dispatch.events.updateCastLegacy({
            id: params.cast.id,
            event_id: params.cast.event_id,
            messages: [{
              id: 'tally_roomactive_text',
              icon: 'exclamation'
            }],
            invited_casters: invitedCasters,
            start_date: new Date()
          })
        }
        await fb.db.collection(CollectionNames.CEFAPPLIANCE).doc(id).set(payload, { merge: true })
        if (params.cast !== null) {
          addCastStatusChange(params.cast.id, {
            cefApplianceId: state.cefApplianceId,
            status: CefApplianceCastStatusChangeType.Assigned,
            teamId: store.state.team.activeTeam
          })
        }
      } catch (error) {
        console.error(error)
      }
    },
    async switchOutputRapidSwitch (context: CefApplianceContext, cefId: string) {
      const { state } = cefApplianceModuleActionContext(context)
      const id = await checkIfApplianceExistsOrThrowErrors(state)
      const update: Partial<CefAppliance> = {
        inputselection: cefId
      }
      const result = await studio.db.collection(CollectionNames.CEFAPPLIANCE).doc(id).set(update, MergeOption.MERGE)
      result.logIfError('Could not switch output')
    },
    async switchOutput (context: CefApplianceContext, cefId: string) {
      const { state } = cefApplianceModuleActionContext(context)
      const id = await checkIfApplianceExistsOrThrowErrors(state)
      const renderers = state.cefAppliance?.cefrenderers
      if (state.cefAppliance === null || renderers === undefined) {
        throw new Error('switchOutput::renderers is not suppose to be undefined')
      }
      if (!Object.keys(renderers).includes(cefId)) {
        throw new Error('cefId is not a valid id for a cefrenderer')
      }
      const currentCastId = renderers[state.cefAppliance.inputselection].castId
      const newCastId = renderers[cefId].castId
      if (newCastId) {
        clearTallyTimeout()
        const delay = cefId === state.cefAppliance.inputpreload ? state.cefAppliance.shortStingerSwitch
                                                                : state.cefAppliance.longStingerSwitch
        roomLiveTallyTimeout = window.setTimeout(() => {
          if (cefId === state.cefAppliance?.inputpreload) {
            studio.db.collection(CollectionNames.CEFAPPLIANCE).doc(id).set({
              inputpreload: null
            }, MergeOption.MERGE).then((result) => result.logIfError('Could not update inputpreload'))
                                 .catch((error) => console.error(error))
          }
          store.dispatch.casts.setTally({ castId: newCastId, message: {
            id: 'tally_roomlive_text',
            icon: 'circle',
            color: 'var(--color-primary)'
          }}).then(result => {
            if (!result.isSuccess) {
              console.error('Set tally live failed', result.error)
            }
          }).catch((error) => console.error('Set tally live failed', error))
        }, delay)
        addCastStatusChange(newCastId, {
          cefApplianceId: state.cefApplianceId,
          status: CefApplianceCastStatusChangeType.Live,
          teamId: store.state.team.activeTeam
        })
      }
      if (currentCastId) {
        const result = await store.dispatch.casts.setTally({ castId: currentCastId, message: {
          id: 'tally_roomactive_text',
          icon: 'exclamation'
        }})
        if (!result.isSuccess) {
          console.error('Could not set Tally after switching output', result.error)
        }
        addCastStatusChange(currentCastId, {
          cefApplianceId: state.cefApplianceId,
          status: CefApplianceCastStatusChangeType.Assigned,
          teamId: store.state.team.activeTeam
        })
      }
      const update: Partial<CefAppliance> = {
        inputselection: cefId
      }
      const result = await studio.db.collection(CollectionNames.CEFAPPLIANCE).doc(id).set(update, MergeOption.MERGE)
      result.logIfError('Could not switch output')
    },
    async preload (context: CefApplianceContext, cefId: string|null) {
      const { state } = cefApplianceModuleActionContext(context)
      if (state.cefApplianceId === null) {
        console.error('preload called without cefApplianceId')
        return
      }
      const result = await studio.db.collection(CollectionNames.CEFAPPLIANCE).doc(state.cefApplianceId).set({
        inputpreload: cefId
      }, MergeOption.MERGE)
      result.logIfError('Could not switch preload in cef appliance')
    },
    toggleCasterPreview (context: CefApplianceContext, params: { userId: string, enabled: boolean }) {
      const { commit } = cefApplianceModuleActionContext(context)
      commit.toggleCasterPreview(params)
    },
    setCefAppUserType (context: CefApplianceContext, type: CefAppUserType) {
      const { commit } = cefApplianceModuleActionContext(context)
      commit.setCefAppUserType(type)
    }
  },
  mutations: {
    setCurrentCefApplianceId (state: CefApplianceState, id: string) {
      state.cefApplianceId = id
    },
    updateCefAppliance (state: CefApplianceState, cefAppliance: CefAppliance) {
      if (state.cefAppliance === null) {
        Vue.set(state, 'cefAppliance', cefAppliance)
      } else {
        lazyUpdate(state, 'cefAppliance', cefAppliance)
      }
    },
    updateCefApplianceList (state: CefApplianceState, snap: firebase.firestore.QuerySnapshot) {
      const newCefAppliances: KeyDict<Partial<CefAppliance>> = {}
      snap.forEach(doc => {
        const applianceInfo = CefAppliance.load(doc.data() ?? {})
        if (!applianceInfo.isSuccess) {
          console.log(`Could not load appliance with id ${doc.id}`, applianceInfo.message)
          return
        }
        if (applianceInfo.value.deleted === true) return
        newCefAppliances[doc.id] = applianceInfo.value
      })
      lazyUpdate(state, 'cefApplianceList', newCefAppliances)
    },
    clearCefAppliance (state: CefApplianceState) {
      state.cefAppliance = null
      state.cefApplianceId = null
    },
    toggleCasterPreview (state: CefApplianceState, params: { userId: string, enabled?: boolean|undefined }) {
      if (params.enabled) {
        state.showPreviewCasterIds.push(params.userId)
        if (state.showPreviewCasterIds.length > MAX_CASTER_PREVIEWS) {
          state.showPreviewCasterIds.shift()
        }
      } else {
        state.showPreviewCasterIds = state.showPreviewCasterIds.filter((userId) => userId !== params.userId)
      }
    },
    setCefAppUserType (state: CefApplianceState, type: CefAppUserType) {
      state.cefAppUserType = type
    }
  }
})

export default cefApplianceModule

export const cefApplianceModuleActionContext = (context: CefApplianceContext) => moduleActionContext(context, cefApplianceModule)
